<template>
  <section class="hero">
    <div class="hero-grid">
      <!-- Columna izquierda: contenido y CTA -->
      <div class="hero-content">
        <h1 class="hero-title">
          Impresión 3D, automatización y aplicativos web a la medida de tu negocio.
        </h1>

        <p class="hero-subtitle">
          Fabricamos tus piezas, automatizamos tus procesos y construimos el
          software que los sostiene. Cuéntanos qué necesitas y te respondemos rápido.
        </p>

        <div class="hero-cta-group">
          <router-link to="/contact" class="hero-cta" aria-label="Solicita tu cotización">
            <span>Solicita tu cotización</span>
            <span class="hero-cta-icon">
              <ArrowRight :size="20" :stroke-width="3" />
            </span>
          </router-link>

          <WhatsAppButton variant="inline" label="Escríbenos por WhatsApp" />
        </div>
      </div>

      <!-- Columna derecha: video de marca -->
      <div class="hero-visual">
        <div class="hero-glow" aria-hidden="true"></div>
        <video
          ref="heroVideo"
          class="hero-video"
          autoplay
          loop
          muted
          playsinline
        >
          <source :src="videoSrc" type="video/mp4" />
        </video>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ArrowRight } from 'lucide-vue-next';
import WhatsAppButton from './WhatsAppButton.vue';
import videoSrc from '../assets/hero/selk-animation.mp4';

export default {
  name: 'HeroSection',
  components: { ArrowRight, WhatsAppButton },
  setup() {
    const heroVideo = ref(null);
    onMounted(() => {
      const v = heroVideo.value;
      if (!v) return;
      // Reproduce a la mitad de velocidad: transiciones más lentas.
      v.playbackRate = 0.5;
      // Accesibilidad: pausa si el usuario prefiere menos movimiento.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) v.pause();
    });
    return { heroVideo, videoSrc };
  },
};
</script>

<style scoped>
.hero {
  display: flex;
  align-items: center;
  min-height: 56vh;
  padding: 1.4vw 0 3.5vw 0;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2vw;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;       /* centra el bloque y reduce el espacio vacío lateral */
}

/* ---------- Columna izquierda ---------- */
.hero-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 36rem;
  /* Permite que la columna encoja dentro del grid y mantiene el texto por
     encima del visual (antes el titular quedaba tapado por el video). */
  min-width: 0;
  position: relative;
  z-index: 2;
}

.hero-title {
  font-family: var(--font-display);
  font-weight: 700;
  color: var(--color-title);
  font-size: 64px;
  line-height: 1.05;
  letter-spacing: -2px;
  margin: 0 0 1.2rem 0;
  text-align: left;
}

.hero-subtitle {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 19px;
  line-height: 1.5;
  color: var(--color-text);
  margin: 0 0 2rem 0;
  text-align: left;
  max-width: 30rem;
}

/* Contacto de primer nivel: cotización formal + WhatsApp (R7). */
.hero-cta-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}

/* CTA de marca (azul acero) con micro-interacción de elevación */
.hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  background-color: var(--color-action);
  color: var(--color-white);
  font-family: var(--font-body);
  font-size: 18px;
  text-decoration: none;
  padding: 0.9rem 1.4rem;
  border-radius: 14px;
  box-shadow: 0px 10px 25px -8px rgba(82, 109, 130, 0.6);
  transition: background-color 0.2s ease, transform 0.25s ease, box-shadow 0.25s ease;
}
.hero-cta:hover {
  background-color: var(--color-action-hover);
  transform: translateY(-3px);
  box-shadow: 0px 14px 30px -8px rgba(82, 109, 130, 0.7);
}
.hero-cta-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-white);
  color: var(--color-action);
  border-radius: 9999px;
  padding: 0.35rem;
  transition: transform 0.25s ease;
}
.hero-cta:hover .hero-cta-icon { transform: translateX(4px); }

/* ---------- Columna derecha (video) ---------- */
.hero-visual {
  position: relative;
  width: 100%;
  max-width: 720px;
  min-width: 0;
  margin: 0 auto;        /* centrado en su columna: aprovecha el espacio vacío */
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Resplandor teal difuminado de fondo */
.hero-glow {
  position: absolute;
  inset: 4%;
  background: radial-gradient(circle, rgba(46, 138, 153, 0.30), rgba(46, 138, 153, 0) 70%);
  filter: blur(40px);
  border-radius: 50%;
}
/* Video enmarcado en óvalo (recorta las esquinas, p. ej. la marca de la esquina) */
.hero-video {
  position: relative;
  z-index: 1;
  /* Nunca por encima del ancho de su columna: al 130% desbordaba la página
     en móvil y tapaba el titular en escritorio. */
  width: 100%;
  height: auto;
  display: block;
  border-radius: 50%;
  border: 1px solid rgba(157, 178, 199, 0.4);
  box-shadow: 0 18px 45px -12px rgba(46, 138, 153, 0.35);
}

/* ---------- Responsive ---------- */
@media (max-width: 800px) {
  .hero { min-height: auto; text-align: center; }
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
  .hero-content {
    align-items: center;
    text-align: center;
    max-width: 100%;
    order: 1;
  }
  .hero-visual { order: 2; max-width: 440px; margin: 0 auto; }
  /* Escala fluida aprobada (R4): llega a 60px donde cabe y baja en
     pantallas estrechas, donde "automatización" no entra a 60px. */
  .hero-title {
    font-size: clamp(2rem, 11vw, 60px);
    line-height: 1.1;
    letter-spacing: 0;
    text-align: center;
    margin-bottom: var(--space-lg);
    overflow-wrap: anywhere;
  }
  .hero-subtitle { text-align: center; }
  .hero-cta-group { justify-content: center; width: 100%; }
}

@media (max-width: 550px) {
  .hero-cta-group { flex-direction: column; align-items: stretch; }
  .hero-cta { width: 100%; justify-content: center; box-sizing: border-box; }
}
</style>
